import { FormikHelpers, useFormik } from 'formik';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { ObjectSchema } from 'yup';

import { SendDetailsForm } from '../../../components';
import { routes } from '../../../constants/routing';
import {
  DELAYED_SHIPPING_URL,
  getSendDetailsFormValidationSchema,
  INITIAL_DETAILS_FORM_STATE as initialFormValues,
} from '../../../constants/shipping';
import { MESSAGE } from '../../../constants/templates';
import { fetchFulfillmentCentersRequest } from '../../../store/actions/fulfillmentCenters';
import { populateReportCampaignSummariesRequest } from '../../../store/actions/reports';
import { selectHasItemsWithEnabledShippingOptions } from '../../../store/selectors/fulfillmentCenters';
import { selectIsSingleDeliveryReportsFetching } from '../../../store/selectors/reports';
import { MaintenanceFormStateEnum } from '../../../types/forms';
import { EngagementSourceTypesEnum, IReportWithCampaignSummaries } from '../../../types/reports';
import { IReportsRouteParams } from '../../../types/routing';
import { IReceiverInfoDetailsFormValues, ISendDetailsFormValues } from '../../../types/shipping';
import { getRequiredFields } from '../../../utils/form';
import { isRealCampaignId, splitPickCampaignIds } from '../../../utils/marketplaceSend';
import { getEditableSendFields, isAwaitingAddress, isCustomReport, isReportViewed } from '../../../utils/reports';

export interface IEditEngagementDetailsSidebarProps {
  onCancel: () => void;
  onSubmit: (values: IReceiverInfoDetailsFormValues) => void;
  report: IReportWithCampaignSummaries | null | undefined;
}

export interface IEditDetailsRef extends Partial<IReceiverInfoDetailsFormValues> {
  getValues: () => IReceiverInfoDetailsFormValues;
}

const EditEngagementDetailsContainer: React.ForwardRefExoticComponent<
  React.PropsWithoutRef<IEditEngagementDetailsSidebarProps> & React.RefAttributes<IEditDetailsRef>
> = React.forwardRef(({ report, onCancel, onSubmit }, ref?: React.Ref<IEditDetailsRef>) => {
  const dispatch = useDispatch();
  const { reportId, type } = useParams<IReportsRouteParams>();

  const isShippingOptionEnabled = useSelector(selectHasItemsWithEnabledShippingOptions(report?.items));
  const isLoading = useSelector(selectIsSingleDeliveryReportsFetching);

  const initialValues = React.useMemo(
    () => ({
      ...initialFormValues,
      ...getEditableSendFields(report),
    }),
    [report],
  );

  const isAddressFixed = React.useMemo(() => report?.is_receiver_address_fixed, [report]);

  const isDelayedShipping = React.useMemo(() => !!report?.[DELAYED_SHIPPING_URL], [report]);

  // Phone is optional only while the send is awaiting an address (or has been viewed but not yet
  // addressed). Once the status moves on to any other value, phone becomes required again.
  const isPhoneOptional = React.useMemo(
    () => (report ? isAwaitingAddress(report) || isReportViewed(report) : false),
    [report],
  );

  const validationSchema = React.useMemo(
    () => getSendDetailsFormValidationSchema({ isPhoneOptional }),
    [isPhoneOptional],
  );

  const handleSubmit = React.useCallback(
    (
      values: IReceiverInfoDetailsFormValues,
      { resetForm, setSubmitting }: FormikHelpers<IReceiverInfoDetailsFormValues>,
    ) => {
      if (!validationSchema.isValidSync(values)) {
        return;
      }

      const successfulSubmit = () => {
        resetForm();
        setSubmitting(false);
      };

      return new Promise<void>(() => {
        setSubmitting(true);
        onSubmit(values);
      }).then(successfulSubmit);
    },
    [onSubmit, validationSchema],
  );

  const form = useFormik<IReceiverInfoDetailsFormValues>({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });

  React.useImperativeHandle(
    ref,
    () => ({
      getValues: () => form.values,
    }),
    [form.values],
  );

  const requiredFields = React.useMemo(
    () => getRequiredFields(validationSchema as ObjectSchema<IReceiverInfoDetailsFormValues>),
    [validationSchema],
  );

  const isNoteHidden = React.useMemo(() => {
    if (report && isCustomReport(report)) {
      return !initialValues[MESSAGE];
    }

    if (report?.pick_campaigns) {
      return report.pick_campaigns.every((campaign) => campaign.disable_handwritten_note_step);
    }

    return true;
  }, [report, initialValues]);

  const isDirectSend = React.useMemo(
    () => report?.engagement_source_type === EngagementSourceTypesEnum.DirectSend,
    [report],
  );

  const handleFormFieldChange = React.useCallback(
    (name: keyof ISendDetailsFormValues, value: string | number | undefined | string[] | boolean) => {
      form.setFieldValue(name, value, true);
    },
    [form.setFieldValue],
  );

  React.useEffect(() => {
    dispatch(fetchFulfillmentCentersRequest());
  }, [dispatch]);

  React.useEffect(() => {
    if (reportId) {
      const { campaignIds } = splitPickCampaignIds(report?.pick_campaign_ids);

      if (campaignIds.length && !report?.pick_campaigns?.length) {
        dispatch(populateReportCampaignSummariesRequest({ campaign_ids: campaignIds, reportId, type }));
      } else if (report && isRealCampaignId(report.pre_created_engagement_id) && !report.pick_campaigns?.length) {
        dispatch(
          populateReportCampaignSummariesRequest({ campaign_ids: [report.pre_created_engagement_id], reportId, type }),
        );
      }
    }
  }, [report?.pick_campaign_ids, report?.pre_created_engagement_id, report?.pick_campaigns, dispatch, reportId]);

  return (
    <SendDetailsForm
      root={routes.reports.root}
      requiredFields={requiredFields}
      form={form}
      onChange={handleFormFieldChange}
      mode={MaintenanceFormStateEnum.Editing}
      onCancel={onCancel}
      onSubmit={form.handleSubmit}
      isEditingShipmentOptionsAllowed={isShippingOptionEnabled}
      isNoteHidden={isNoteHidden}
      isDirectSend={isDirectSend}
      isAddressFixed={isAddressFixed}
      isDelayedShipping={isDelayedShipping}
      isLoading={isLoading}
    />
  );
});

export default EditEngagementDetailsContainer;
